/**
 * Supabase Auth — the browser's ONLY remaining reason to talk to Supabase
 * directly (Phase 3, docs/ROADMAP.md). The backend is server-authoritative
 * and requires a valid Supabase bearer token on every `/game/*` request (see
 * apps/backend/src/auth/supabase-auth.guard.ts); this module owns minting
 * and holding that session and handing out its access token.
 *
 * It has no opinion on game state or the `saves` table — `backend-client.ts`
 * is the only thing that talks to `/game/*`, and the backend's service-role
 * client is the only thing that still touches `saves` (see
 * supabase/migrations/0002_saves_lockdown.sql).
 *
 * The Supabase client is injected behind a narrow structural interface so
 * tests can drive this with a fake, mirroring the pattern the old
 * supabase-store.ts used.
 */

import { createClient } from '@supabase/supabase-js';
import { getSupabaseConfig } from './supabase-config';

export interface SupabaseSessionLike {
  access_token: string;
  user: { id: string; is_anonymous?: boolean; email?: string | null };
}

export interface SupabaseAuthClientLike {
  auth: {
    getSession(): Promise<{ data: { session: SupabaseSessionLike | null } }>;
    /**
     * Like `getSession()`, but re-mints the access token from the server
     * instead of returning whatever is cached locally. `linkIdentity()`
     * completes via a full-page redirect through Google and back — a tab
     * that never lands on that return leg (wrong redirect URL, closed
     * early, etc.) keeps its pre-link token forever, `is_anonymous: true`
     * baked in, even though the account is already linked server-side.
     * `getAuthStatus()` uses this instead of `getSession()` specifically so
     * a plain reload self-heals that stuck state.
     */
    refreshSession(): Promise<{ data: { session: SupabaseSessionLike | null } }>;
    signInAnonymously(): Promise<{
      data: { user: { id: string } | null; session: SupabaseSessionLike | null };
      error: { message: string } | null;
    }>;
    /**
     * Upgrades the CURRENT session to a Google identity, keeping the same
     * `auth.uid()` — and therefore the same backend `saves` row — instead of
     * minting a second, empty account. Redirects the browser to Google; the
     * promise only rejects on errors before that redirect happens (e.g. the
     * provider isn't enabled in the Supabase dashboard yet).
     */
    linkIdentity(credentials: {
      provider: 'google';
      options?: { redirectTo?: string };
    }): Promise<{ data: { url: string | null }; error: { message: string } | null }>;
    signUp(credentials: {
      email: string;
      password: string;
    }): Promise<{
      data: { user: { id: string } | null; session: SupabaseSessionLike | null };
      error: { message: string } | null;
    }>;
    signInWithPassword(credentials: {
      email: string;
      password: string;
    }): Promise<{
      data: { user: { id: string } | null; session: SupabaseSessionLike | null };
      error: { message: string } | null;
    }>;
    signInWithOAuth(credentials: {
      provider: 'google';
      options?: { redirectTo?: string };
    }): Promise<{ data: { url: string | null }; error: { message: string } | null }>;
    /** Upgrades the CURRENT (usually anonymous) session in place — same `auth.uid()`, same backend save. */
    updateUser(attributes: {
      email?: string;
      password?: string;
    }): Promise<{ data: { user: { id: string } | null }; error: { message: string } | null }>;
    resetPasswordForEmail(
      email: string,
      options?: { redirectTo?: string },
    ): Promise<{ error: { message: string } | null }>;
    signOut(): Promise<{ error: { message: string } | null }>;
    onAuthStateChange(
      callback: (event: string, session: SupabaseSessionLike | null) => void,
    ): { data: { subscription: { unsubscribe: () => void } } };
  };
}

/**
 * Which identity the current session is tied to. 'anonymous' is the
 * automatic first-run state — a random per-device account that a cleared
 * browser or a different machine can never reach again. 'google' means the
 * player deliberately linked a real account, so the same save follows them
 * across browsers, devices and cleared storage.
 */
export interface AuthStatus {
  mode: 'anonymous' | 'google';
  /** Set when `mode` is 'google' and Supabase returned one. */
  email: string | null;
}

export class SupabaseAuth {
  constructor(private readonly client: SupabaseAuthClientLike) {}

  /** Resume the device's session or mint an anonymous one. */
  private async ensureSession(): Promise<SupabaseSessionLike> {
    const { data } = await this.client.auth.getSession();
    if (data.session) {
      return data.session;
    }
    const anon = await this.client.auth.signInAnonymously();
    if (anon.error || !anon.data.user || !anon.data.session) {
      throw new Error(`Supabase-Anmeldung fehlgeschlagen: ${anon.error?.message ?? 'kein User'}`);
    }
    return anon.data.session;
  }

  /** The bearer token every backend request attaches. */
  async getAccessToken(): Promise<string> {
    const session = await this.ensureSession();
    return session.access_token;
  }

  async getAuthStatus(): Promise<AuthStatus> {
    await this.ensureSession();
    const { data } = await this.client.auth.refreshSession();
    const user = data.session?.user;
    // `is_anonymous` is `false` only once a real identity (Google) has been
    // linked; missing/true both mean the automatic anonymous session.
    if (user && user.is_anonymous === false) {
      return { mode: 'google', email: user.email ?? null };
    }
    return { mode: 'anonymous', email: null };
  }

  async linkGoogleAccount(): Promise<void> {
    await this.ensureSession();
    const redirectTo = typeof window !== 'undefined' ? window.location.href : undefined;
    const { error } = await this.client.auth.linkIdentity({
      provider: 'google',
      options: redirectTo ? { redirectTo } : undefined,
    });
    if (error) throw new Error(`Google-Verknüpfung fehlgeschlagen: ${error.message}`);
  }

  /**
   * Checks for an existing session WITHOUT creating one — unlike every method
   * above, which goes through `ensureSession()` and silently mints an
   * anonymous session on first call. This is what `AuthGate` uses to decide
   * "skip straight to the game" vs. "show the entry screen": calling
   * `getAccessToken()` there would defeat the point by auto-creating a guest
   * session before the player has chosen anything.
   */
  async peekSession(): Promise<SupabaseSessionLike | null> {
    const { data } = await this.client.auth.getSession();
    return data.session;
  }

  /** Fresh account, no prior session. For an already-anonymous session that should keep its save, use `upgradeToEmailAccount`. */
  async signUp(email: string, password: string): Promise<void> {
    const { error } = await this.client.auth.signUp({ email, password });
    if (error) throw new Error(`Registrierung fehlgeschlagen: ${error.message}`);
  }

  async signInWithPassword(email: string, password: string): Promise<void> {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw new Error('E-Mail oder Passwort falsch.');
  }

  /** Fresh Google sign-in with no session yet — distinct from `linkGoogleAccount()`, which upgrades a live session. Redirects the browser. */
  async signInWithGoogle(): Promise<void> {
    const redirectTo = typeof window !== 'undefined' ? window.location.href : undefined;
    const { error } = await this.client.auth.signInWithOAuth({
      provider: 'google',
      options: redirectTo ? { redirectTo } : undefined,
    });
    if (error) throw new Error(`Google-Anmeldung fehlgeschlagen: ${error.message}`);
  }

  /** Upgrades the CURRENT (anonymous) session to real credentials in place — same save, same `auth.uid()`, mirrors `linkGoogleAccount()`. */
  async upgradeToEmailAccount(email: string, password: string): Promise<void> {
    await this.ensureSession();
    const { error } = await this.client.auth.updateUser({ email, password });
    if (error) throw new Error(`Konto sichern fehlgeschlagen: ${error.message}`);
  }

  async resetPasswordForEmail(email: string): Promise<void> {
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined;
    const { error } = await this.client.auth.resetPasswordForEmail(email, redirectTo ? { redirectTo } : undefined);
    if (error) throw new Error(`Zurücksetzen fehlgeschlagen: ${error.message}`);
  }

  /** Used on the recovery screen reached via a password-reset email link. */
  async setNewPassword(password: string): Promise<void> {
    const { error } = await this.client.auth.updateUser({ password });
    if (error) throw new Error(`Passwort setzen fehlgeschlagen: ${error.message}`);
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) throw new Error(`Abmelden fehlgeschlagen: ${error.message}`);
  }

  /** Thin passthrough so `AuthGate` can detect the `PASSWORD_RECOVERY` event Supabase fires when a reset-password email link is opened. */
  onAuthStateChange(callback: (event: string, session: SupabaseSessionLike | null) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange(callback);
    return () => data.subscription.unsubscribe();
  }
}

/** Builds the real, Supabase-SDK-backed auth client for production use. */
export function createSupabaseAuth(): SupabaseAuth {
  const config = getSupabaseConfig();
  if (!config) {
    throw new Error('Supabase ist nicht konfiguriert — der Server ist ohne Anmeldung nicht erreichbar.');
  }
  const client = createClient(config.url, config.key) as unknown as SupabaseAuthClientLike;
  return new SupabaseAuth(client);
}
